import React, { useState, useEffect } from 'react';
import { callsAPI } from '../services/api';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { formatDuration } from '../utils/formatters';
import RatingStars from '../components/RatingStars';

export default function StatsPage() {
  const [stats, setStats] = useState(null);
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    callsAPI.stats({ period }).then(res => { setStats(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, [period]);

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;
  if (!stats) return <div className="empty-state"><p>Ma'lumot yuklanmadi</p></div>;

  const dailyData = (stats.daily_calls || []).map(d => ({
    date: new Date(d.date).toLocaleDateString('uz-UZ', { month: 'short', day: 'numeric' }),
    Javob: parseInt(d.answered), Javobsiz: parseInt(d.missed), Jami: parseInt(d.total),
  }));

  const hourlyData = (stats.hourly_calls || []).map(h => ({
    hour: `${String(parseInt(h.hour)).padStart(2, '0')}:00`,
    Qo_ng_iroqlar: parseInt(h.total),
  }));

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">Statistikalar</h1></div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {['today', 'week', 'month', 'year'].map(p => (
            <button key={p} className={`btn btn-sm ${period === p ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setPeriod(p)}>
              {{ today: 'Bugun', week: 'Hafta', month: 'Oy', year: 'Yil' }[p]}
            </button>
          ))}
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card"><span className="stat-label">Jami qo'ng'iroqlar</span><span className="stat-value">{stats.summary.total_calls}</span></div>
        <div className="stat-card"><span className="stat-label">Javob berildi</span><span className="stat-value" style={{ color: '#10b981' }}>{stats.summary.answered}</span></div>
        <div className="stat-card"><span className="stat-label">Javobsiz</span><span className="stat-value" style={{ color: '#ef4444' }}>{stats.summary.missed}</span></div>
        <div className="stat-card"><span className="stat-label">Javob darajasi</span><span className="stat-value">{stats.summary.answer_rate}%</span></div>
        <div className="stat-card"><span className="stat-label">O'rtacha davomiylik</span><span className="stat-value">{formatDuration(stats.summary.avg_duration)}</span></div>
        <div className="stat-card"><span className="stat-label">O'rtacha kutish</span><span className="stat-value">{formatDuration(stats.summary.avg_wait_time)}</span></div>
      </div>

      <div className="adm-g2" style={{ gap: '20px', marginBottom: '24px' }}>
        <div className="card">
          <div className="card-header"><span className="card-title">Kunlik qo'ng'iroqlar</span></div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dailyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2d3a4f" />
              <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 11 }} />
              <YAxis tick={{ fill: '#64748b', fontSize: 11 }} />
              <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #2d3a4f', borderRadius: '8px', color: '#f1f5f9' }} />
              <Legend />
              <Bar dataKey="Javob" fill="#10b981" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Javobsiz" fill="#ef4444" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <div className="card-header"><span className="card-title">Soatlik faollik</span></div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={hourlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2d3a4f" />
              <XAxis dataKey="hour" tick={{ fill: '#64748b', fontSize: 11 }} />
              <YAxis tick={{ fill: '#64748b', fontSize: 11 }} />
              <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #2d3a4f', borderRadius: '8px', color: '#f1f5f9' }} />
              <Line type="monotone" dataKey="Qo_ng_iroqlar" stroke="#3b82f6" strokeWidth={2} dot={{ fill: '#3b82f6', r: 4 }} name="Qo'ng'iroqlar" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {stats.operator_stats?.length > 0 && (
        <div className="card">
          <div className="card-header"><span className="card-title">Operator ko'rsatkichlari</span></div>
          <div className="table-wrapper"><table><thead><tr>
            <th>Operator</th><th>Raqam</th><th>Jami</th><th>Javob</th><th>Javobsiz</th>
            <th>Jami gaplashish</th><th>O'rtacha davomiylik</th><th>Baho (CSAT)</th>
          </tr></thead><tbody>
            {stats.operator_stats.map((op, i) => (
              <tr key={i}>
                <td style={{ fontWeight: '600', color: 'var(--text-primary)' }}>{op.full_name}</td>
                <td>{op.sip_extension}</td>
                <td>{parseInt(op.total_calls)}</td>
                <td style={{ color: '#10b981' }}>{parseInt(op.answered)}</td>
                <td style={{ color: '#ef4444' }}>{parseInt(op.missed)}</td>
                <td style={{ fontWeight: 600 }}>{formatDuration(parseInt(op.total_talk_time) || 0)}</td>
                <td>{formatDuration(Math.round(parseFloat(op.avg_duration) || 0))}</td>
                <td>
                  {parseInt(op.rated_count) > 0 ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <RatingStars value={op.avg_rating} />
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>({op.rated_count})</span>
                    </span>
                  ) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                </td>
              </tr>
            ))}
          </tbody></table></div>
        </div>
      )}
    </div>
  );
}
